import { useComponentState } from "@faedev/dynamic-values";
import { composeClassNames, ifDefined } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { CSSProperties, useEffect, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { useCookies } from "react-cookie";
import ModalLogin from "src/components/ModalLogin";
import ModalRegister from "src/components/ModalRegister";
import ModalResetPassword from "src/components/ModalResetPassword";
import Paragraph from "src/components/Paragraph";
import Preloader from "src/components/Preloader";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getCookieByName from "src/functions/getCookieByName";
import gutter from "src/functions/gutter";
import useLoggedInUser from "src/functions/useLoggedInUser";
import validateAuthToken from "src/functions/validateAuthToken";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sButtons } from "src/styles/sButtons.module.scss";
import { sSellButtons } from "src/styles/sSellButtons.module.scss";
import { VariantBtnColor } from "src/types/VariantBtnColor";

type Props = {
  className?: (string | string) | null | undefined;
  entityId: string;
  id: number;
  inCart: boolean;
  newPrice?: number;
  orderId?: string;
  size?: ("sm" | "lg") | null | undefined;
  style?: CSSProperties | null | undefined;
  type: "lecture" | "program" | "merch" | "nft";
  btnPrimary?: {
    display?: boolean;
    title?: string;
    variant?: VariantBtnColor | null | undefined;
  };
  btnSecondary?: {
    display?: boolean;
    title?: string;
    variant?: VariantBtnColor | null | undefined;
  };
};

function SellButtons(props: Props) {
  const { push } = useRouter();
  const [success, setSuccess] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loadingAddToCart, setLoadingAddToCart] = useState(false);
  const [loadingBuyNow, setLoadingBuyNow] = useState(false);
  const [inCart, setInCart] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  var userResult = useLoggedInUser();
  var openModalLogin = useComponentState(false);
  var openModalRegister = useComponentState(false);
  var openModalResetPassword = useComponentState(false);
  const [cookies, setCookie] = useCookies(null);

  useEffect(() => {
    setInCart(props.inCart);
  }, [props.inCart]);

  useEffect(() => {
    const timeId = setTimeout(() => {
      // After 10 seconds set the show value to false
      setSuccess(false);
      setFailed(false);
    }, 10000);

    return () => {
      clearTimeout(timeId);
    };
  }, [success, failed]);

  return (
    <>
      <div
        style={props.style}
        className={composeClassNames([
          sButtons,
          sSellButtons,
          props.className,
          props.size,
          "sell-buttons",
        ])}
      >
        {ifDefined(
          props.id,
          (value) =>
            (props?.btnPrimary === undefined ||
              props?.btnPrimary?.display === true) && (
              <Button
                disabled={inCart}
                className={composeClassNames([
                  sButton,
                  props?.btnPrimary?.variant && "btn-shine",
                  "btn-icon-before",
                ])}
                onClick={
                  !inCart
                    ? async function () {
                        setLoadingBuyNow(true);
                        const cookieOrderId = getCookieByName(`orderId`);
                        fetch(`${constants.Paths.apiUrl}/orders`, {
                          method: "POST",
                          headers: userResult.result
                            ? validateAuthToken(getCookieByName(`auth-token`), {
                                "Content-Type": "application/json",
                              })
                            : { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            order_id: cookieOrderId
                              ? cookieOrderId
                              : props.orderId,
                            item: {
                              entityId: props.entityId,
                              entityType: props.type,
                              price: props.newPrice ? props.newPrice : null,
                            },
                          }),
                        }).then(async (res) => {
                          if (res.ok) {
                            setCookie(
                              `orderId`,
                              await res.text(),
                              constants.CookiesOptions
                            );
                            window.location.href = "/cart";
                          } else {
                            // throw new Error("API Error");
                            setFailed(true);
                            setLoadingBuyNow(false);
                            setErrorMessage(
                              `Could not add to cart, please try again.`
                            );
                          }
                        });
                      }
                    : null
                }
                size={props?.size || "lg"}
                variant={
                  props?.btnPrimary?.variant === "shine"
                    ? "warning"
                    : props?.btnPrimary?.variant || "primary"
                }
              >
                {loadingBuyNow ? (
                  <Preloader size="sm" />
                ) : (
                  <Translate tKey={props.btnPrimary?.title || "Buy now"} />
                )}
              </Button>
            ),
          () => null
        )}
        {ifDefined(
          props.id,
          (value) =>
            (props?.btnSecondary === undefined ||
              props?.btnSecondary?.display === true) && (
              <Button
                className={composeClassNames([
                  sButton,
                  props?.btnPrimary?.variant && "btn-shine",
                  "btn-icon-before",
                ])}
                onClick={
                  !inCart
                    ? async function () {
                        setLoadingAddToCart(true);
                        const cookieOrderId = getCookieByName(`orderId`);
                        fetch(`${constants.Paths.apiUrl}/orders`, {
                          method: "POST",
                          headers: userResult.result
                            ? validateAuthToken(getCookieByName(`auth-token`), {
                                "Content-Type": "application/json",
                              })
                            : { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            order_id: cookieOrderId
                              ? cookieOrderId
                              : props.orderId,
                            item: {
                              entityId: props.entityId,
                              entityType: props.type,
                              price: props.newPrice ? props.newPrice : null,
                            },
                          }),
                        }).then(async (res) => {
                          if (res.ok) {
                            setCookie(
                              `orderId`,
                              await res.text(),
                              constants.CookiesOptions
                            );
                            setLoadingAddToCart(false);
                            setSuccess(true);
                            setInCart(true);
                          } else {
                            setLoadingAddToCart(false);
                            setFailed(true);
                            setErrorMessage(
                              `Could not add to cart, please try again.`
                            );
                          }
                        });
                      }
                    : () => {
                        push("/cart");
                      }
                }
                size={props?.size || "lg"}
                variant={
                  props?.btnSecondary?.variant === "shine"
                    ? "warning"
                    : props?.btnSecondary?.variant || "outline-primary"
                }
              >
                {loadingAddToCart ? (
                  <Preloader size="sm" />
                ) : inCart ? (
                  <Translate tKey="Show cart" />
                ) : (
                  <Translate tKey={props.btnPrimary?.title || "Add to cart"} />
                )}
              </Button>
            ),
          () => null
        )}
      </div>
      {success && (
        <Alert
          variant="success"
          className={sAlert}
          style={gutter(`margin`, `vertical`, `md`)}
        >
          <Translate tKey={`Added to cart!`} />
        </Alert>
      )}
      {failed && (
        <Alert
          variant="danger"
          className={sAlert}
          style={gutter(`margin`, `vertical`, `md`)}
        >
          <Translate tKey={errorMessage} />
        </Alert>
      )}
      {/* TODO: context provider for modal login */}
      <ModalLogin
        openModalLogin={openModalLogin}
        openModalRegister={openModalRegister}
        openModalResetPassword={openModalResetPassword}
        alert={
          <Alert variant={`warning`} className={sAlert}>
            <Paragraph
              width={`semibold`}
              align="center"
              style={gutter(`margin`, `all`, `0`)}
            >
              <Translate
                tKey={`You need to sing in to continue shopping. If you do not have an account, sign up first.`}
              />
              <Button
                variant="link"
                size="sm"
                className={sButton}
                onClick={() => {
                  openModalLogin.change(false);
                  openModalRegister.change(true);
                  openModalResetPassword.change(false);
                }}
              >
                <Translate tKey={`Sign up now!`} />
              </Button>
            </Paragraph>
          </Alert>
        }
        key={`ModalLogin`}
      />
      <ModalRegister
        openModalLogin={openModalLogin}
        openModalRegister={openModalRegister}
        key={`ModalRegister`}
      />
      <ModalResetPassword
        openModalResetPassword={openModalResetPassword}
        key={`ModalResetPassword`}
      />
    </>
  );
}

export default SellButtons;
